// Settings › SMTP (wave 3, W2_Portal_Settings look): the mail server the
// platform sends from. Submit calls checkSMTP(fromEmail, details) exactly as
// before (the server tests the details and keeps them); nothing else is
// written here. Only with critical edits on (settings.AllowCriticalEditsAdmin);
// in demo mode every value is hidden. The password is masked until someone
// presses the eye button and is never printed or logged.
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import AlternateEmailRoundedIcon from '@mui/icons-material/AlternateEmailRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { api } from 'common';
import { showToast } from '../components/feedback';
import { tokens as c } from '../theme';
import { SectionCard, SettingRow, calmCaps } from '../components/modern';

// Wide screens (lg and up): the settings in two columns; one column below lg
// as before. The last grid row ends without a line (the button bar draws
// one); the very last row never has one.
const ROWS_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' },
  columnGap: 4,
  '& > :nth-last-of-type(2):nth-of-type(odd)': { borderBottom: { lg: 0 } },
};

const SmtpSettings = () => {
  const { t, i18n } = useTranslation();
  const { checkSMTP } = api;
  const settings = useSelector((state) => state.settingsdata.settings);
  const smtpdata = useSelector((state) => state.smtpdata);
  const [data, setData] = useState();
  const [fromEmail, setFromEmail] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (smtpdata.smtpDetails || smtpdata.fromEmail) {
      setData(smtpdata.smtpDetails);
      setFromEmail(smtpdata.fromEmail || '');
    }
  }, [smtpdata.smtpDetails, smtpdata.fromEmail]);

  const allowed = !!(settings && settings.AllowCriticalEditsAdmin);
  const hidden = t('hidden_demo');

  const handleTextChange = (name, value) => {
    if (name === 'pass' || name === 'user') {
      setData({ ...data, auth: data?.auth ? { ...data.auth, [name]: value } : { [name]: value } });
    } else if (name === 'port') {
      setData({ ...data, port: parseInt(value) });
    } else {
      setData({ ...data, [name]: value });
    }
  };

  const handleSubmit = () => {
    if (checking) return;
    if (allowed) {
      setChecking(true);
      checkSMTP(fromEmail, data)
        .then((res) => {
          if (res.success) {
            showToast.success(t('success'));
          } else {
            showToast.error(t('smtp_error'));
          }
        })
        .finally(() => setChecking(false));
    } else {
      showToast.warning(t('demo_mode'));
    }
  };

  const field = (id, label, value, onChange, extra) => (
    <SettingRow
      id={`smtp-${id}`}
      label={label}
      control={
        <TextField
          id={`smtp-${id}`}
          size="small"
          value={allowed ? value : hidden}
          disabled={!allowed}
          onChange={(e) => onChange(e.target.value)}
          sx={{ width: { xs: '100%', sm: 380 } }}
          {...(extra || {})}
        />
      }
    />
  );

  const passLabel = showPass ? t('hide', { defaultValue: 'Hide' }) : t('show', { defaultValue: 'Show' });

  return (
    <Box dir={i18n.dir()}>
      <SectionCard id="smtp" icon={<AlternateEmailRoundedIcon />} hue="info" title={t('smtp', { defaultValue: 'SMTP' })}>
        <Box sx={{ mt: 1, ...ROWS_SX }}>
          {field('fromEmail', t('fromEmail'), fromEmail, setFromEmail, { inputProps: { inputMode: 'email', spellCheck: false, autoComplete: 'off' } })}
          {field('host', t('host'), data && data.host ? data.host : '', (v) => handleTextChange('host', v), { inputProps: { spellCheck: false, autoComplete: 'off' } })}
          {field('port', t('port'), data && data.port ? data.port.toString() : '', (v) => handleTextChange('port', v), { inputProps: { inputMode: 'numeric', autoComplete: 'off' } })}
          {field('user', t('username'), data && data.auth && data.auth.user ? data.auth.user : '', (v) => handleTextChange('user', v), { inputProps: { spellCheck: false, autoComplete: 'off' } })}
          {field('pass', t('password'), data && data.auth && data.auth.pass ? data.auth.pass : '', (v) => handleTextChange('pass', v), {
            type: allowed && !showPass ? 'password' : 'text',
            autoComplete: 'new-password',
            InputProps: allowed
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={passLabel}>
                        <IconButton edge="end" size="small" aria-label={`${passLabel}: ${t('password')}`} aria-pressed={showPass ? 'true' : 'false'} onClick={() => setShowPass((v) => !v)}>
                          {showPass ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }
              : undefined,
          })}
          <SettingRow
            id="smtp-secure"
            layout="switch"
            label={t('secure')}
            control={
              <Switch
                id="smtp-secure"
                checked={data && data.secure ? data.secure : false}
                onChange={(e) => setData({ ...data, secure: e.target.checked })}
                sx={{ my: -0.75 }}
              />
            }
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2, mt: 1, borderTop: `1px solid ${c.OUTLINE}` }}>
          <Button
            variant="contained"
            startIcon={checking ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon />}
            onClick={handleSubmit}
            disabled={checking}
          >
            {checking ? t('smtp_checking', { defaultValue: 'Checking…' }) : calmCaps(t('submit'))}
          </Button>
        </Box>
      </SectionCard>
    </Box>
  );
};

export default SmtpSettings;
